import React from "react";
import { Form, Input, Select, TextArea, Checkbox, Label } from "semantic-ui-react";
import { TrajectoryChart } from "./TrajectoryChart";
import "./Paso4Objetivos.scss";

const fmt = (v, d = 1) => (isFinite(v) ? Number(v).toLocaleString("es-AR", { minimumFractionDigits: d, maximumFractionDigits: d }) : "—");

// `r.years` (año objetivo − año base) tiene que ser positivo para que
// totalPercent/targetEmissions signifiquen algo — si el año objetivo
// quedó antes (o igual) que el año base, targetRate*years da negativo y
// mostrar "−" + ese negativo termina en un doble signo ("--8,4%") en vez
// de avisar que el dato está mal cargado.
const validYears = (r) => r.years > 0;
// "%" de reducción con el signo ya resuelto — nunca dos signos, y "—"
// cuando el rango de años no tiene sentido (en vez de un número que
// aparenta ser válido pero no lo es).
const pctReduction = (r) => (validYears(r) ? `−${fmt(r.totalPercent)}` : "—");

const METHOD_OPTIONS_12 = [
  { key: "abs", value: "abs", text: "Contracción absoluta (recomendado sector)" },
  { key: "fisica", value: "fisica", text: "Intensidad física (p. ej., por par/prenda)" },
  { key: "re", value: "re", text: "Electricidad renovable (alternativa A2)" },
];
const METHOD_OPTIONS_3 = [
  { key: "abs", value: "abs", text: "Contracción absoluta" },
  { key: "fisica", value: "fisica", text: "Intensidad física" },
  { key: "geva", value: "geva", text: "Intensidad económica (GEVA)" },
  { key: "prov", value: "prov", text: "Compromiso de proveedores" },
];
const AMBITION_OPTIONS_3 = [
  { key: "wb2", value: "wb2", text: "Well-below 2 °C (mín., 2,5%/año)" },
  { key: "1.5", value: "1.5", text: "1.5 °C (alentado, 4,2%/año)" },
];

// Portado de vP4() del prototipo (línea ~703).
export function Paso4Objetivos({ record, computed, patchSection }) {
  const targets = record.targets || {};
  const s12 = targets.scope12 || {};
  const s3 = targets.scope3 || {};
  const nz = targets.netZero || {};
  const params = computed?.params || {};
  const calc = computed?.calc || {};
  const r12 = computed?.targetScope12 || {};
  const r3 = computed?.targetScope3 || {};
  const t12 = computed?.statementScope12 || { es: "", en: "" };
  const t3 = computed?.statementScope3 || { es: "", en: "" };

  const setTargets = (patch) => patchSection("targets", { ...targets, ...patch }, { immediate: true });
  const setS12 = (patch) => setTargets({ scope12: { ...s12, ...patch } });
  const setS3 = (patch) => setTargets({ scope3: { ...s3, ...patch } });
  const setNz = (patch) => setTargets({ netZero: { ...nz, ...patch } });

  const series12 = (record.tracking || []).map((x) => ({ year: Number(x.year), value: (parseFloat(x.scope1) || 0) + (parseFloat(x.scope2) || 0) }));
  const series3 = (record.tracking || []).map((x) => ({ year: Number(x.year), value: parseFloat(x.scope3Covered) || 0 }));

  const timelineBadge = (r, targetYear) => {
    if (!targetYear) return null;
    if (!validYears(r)) {
      return (
        <Label color="red" className="sbti-paso4__years-badge">
          El año objetivo tiene que ser posterior al año base
        </Label>
      );
    }
    return (
      <Label color={r.timelineOK && r.baseYearOK ? "green" : "red"} className="sbti-paso4__years-badge">
        {r.years} años desde base · {r.yearsFromNow} desde hoy{!r.timelineOK ? " ⇢ fuera de rango" : ""}{!r.baseYearOK ? " · base <2015" : ""}
      </Label>
    );
  };

  return (
    <div className="sbti-paso4">
      <div className="sbti-pagehead">
        <div className="sbti-crumb">Paso 4 · Diseño</div>
        <h2>Diseño de objetivos <span className="sbti-pgram sbti-pgram--mid"><span>near-term</span></span></h2>
        <p className="sbti-sub">
          Métodos aplicables al sector según la guía Apparel &amp; Footwear, con la ambición mínima vigente. El
          plazo near-term es de {params.plazo_min ?? 5} a {params.plazo_max ?? 10} años desde la presentación.
        </p>
      </div>

      <div className="sbti-note sbti-note--warn">
        <b>Actualización de criterios respecto de la guía sectorial 2019:</b> para A1+2 el mínimo es{" "}
        <b>1.5 °C ({params.tasa_15C ?? 4.2}% anual)</b>; para A3 el mínimo es{" "}
        <b>well-below 2 °C ({params.tasa_WB2C ?? 2.5}% anual)</b>; el plazo es <b>5–10 años</b> con año base ≥ 2015.
      </div>

      {/* ── A1 + A2 ── */}
      <div className="sbti-card">
        <h3><span className="sbti-tag">A1 + A2</span> Objetivo de alcances 1 y 2 — obligatorio para todos</h3>
        <Form>
          <Form.Group widths={3}>
            <Form.Field>
              <label>Método</label>
              <Select options={METHOD_OPTIONS_12} value={s12.method || "abs"} onChange={(_, d) => setS12({ method: d.value })} />
            </Form.Field>
            <Form.Field>
              <label>Ambición</label>
              <Input value="1.5 °C — mínimo vigente" disabled />
            </Form.Field>
            <Form.Field>
              <label>Año objetivo</label>
              <Input type="number" value={s12.targetYear || ""} onChange={(_, d) => setS12({ targetYear: d.value })} />
              {timelineBadge(r12, s12.targetYear)}
            </Form.Field>
          </Form.Group>
        </Form>

        {s12.method !== "re" ? (
          <>
            <div className="sbti-kpis">
              <div className="sbti-kpi"><div className="sbti-kpi__l">Base A1+2</div><div className="sbti-kpi__v">{fmt(r12.baseEmissions)} <span>tCO₂e</span></div></div>
              <div className="sbti-kpi sbti-kpi--alert"><div className="sbti-kpi__l">Reducción total</div><div className="sbti-kpi__v">{pctReduction(r12)} <span>%</span></div></div>
              <div className="sbti-kpi"><div className="sbti-kpi__l">Emisiones al {s12.targetYear || "—"}</div><div className="sbti-kpi__v">{validYears(r12) ? fmt(r12.targetEmissions) : "—"} <span>tCO₂e</span></div></div>
            </div>
            <div className="sbti-chartbox">
              <TrajectoryChart baseYear={Number(record.inventory?.baseYear)} targetYear={Number(s12.targetYear)} baseEmissions={r12.baseEmissions} targetEmissions={r12.targetEmissions} series={series12} />
            </div>
          </>
        ) : (
          <>
            <div className="sbti-note">
              <b>Alternativa de electricidad renovable:</b> {params.re_hito}. Quien ya alcanzó el umbral debe
              mantener o aumentar su participación. Combinable con reducción de A1.
            </div>
            <Form>
              <Form.Field className="sbti-paso4__narrow">
                <label>% renovable actual</label>
                <Input type="number" step="0.1" value={s12.currentRenewablePercent || ""} onChange={(_, d) => setS12({ currentRenewablePercent: d.value })} />
              </Form.Field>
            </Form>
          </>
        )}
        <p className="sbti-hint">{r12.detail}</p>
        <div className="sbti-note">
          <b>Redacción oficial generada:</b><br />ES — {t12.es}<br />EN — {t12.en}
        </div>
      </div>

      {/* ── A3 ── */}
      <div className="sbti-card">
        <h3><span className="sbti-tag">A3</span> Objetivo de alcance 3 — {calc.scope3Required ? `obligatorio (A3 ≥ ${params.umbral_a3 ?? 40}%)` : "no obligatorio con los datos actuales"}</h3>
        <Form>
          <Form.Group widths={4}>
            <Form.Field>
              <label>Método</label>
              <Select options={METHOD_OPTIONS_3} value={s3.method || "abs"} onChange={(_, d) => setS3({ method: d.value })} />
            </Form.Field>
            <Form.Field>
              <label>Ambición (si absoluta)</label>
              <Select options={AMBITION_OPTIONS_3} value={s3.ambition || "wb2"} onChange={(_, d) => setS3({ ambition: d.value })} />
            </Form.Field>
            <Form.Field>
              <label>Año objetivo</label>
              <Input type="number" value={s3.targetYear || ""} onChange={(_, d) => setS3({ targetYear: d.value })} />
              {timelineBadge(r3, s3.targetYear)}
            </Form.Field>
            <Form.Field>
              <label>Base del objetivo</label>
              <Input value={`${fmt(calc.coveredEmissions)} tCO₂e (cobertura Paso 2)`} disabled />
            </Form.Field>
          </Form.Group>
        </Form>

        {(s3.method === "abs" || s3.method === "fisica") && (
          <>
            <div className="sbti-kpis">
              <div className="sbti-kpi sbti-kpi--alert"><div className="sbti-kpi__l">Reducción {s3.method === "fisica" ? "(absoluta subyacente)" : ""}</div><div className="sbti-kpi__v">{pctReduction(r3)} <span>%</span></div></div>
              <div className="sbti-kpi"><div className="sbti-kpi__l">Emisiones cubiertas al {s3.targetYear || "—"}</div><div className="sbti-kpi__v">{validYears(r3) ? fmt(r3.targetEmissions) : "—"} <span>tCO₂e</span></div></div>
            </div>
            <div className="sbti-chartbox">
              <TrajectoryChart baseYear={Number(record.inventory?.baseYear)} targetYear={Number(s3.targetYear)} baseEmissions={r3.baseEmissions} targetEmissions={r3.targetEmissions} series={series3} />
            </div>
            {s3.method === "fisica" && (
              <div className="sbti-note">
                <b>Condición de la intensidad física:</b> mejora mínima del {params.fisica_piso ?? 2}% anual lineal
                de intensidad manteniendo las emisiones absolutas en o bajo el nivel del año base.
              </div>
            )}
          </>
        )}

        {s3.method === "geva" && (
          <>
            <div className="sbti-kpis">
              <div className="sbti-kpi sbti-kpi--alert"><div className="sbti-kpi__l">Reducción de intensidad (tCO₂e/$VA)</div><div className="sbti-kpi__v">{pctReduction(r3)} <span>% en {r3.years || "—"} años</span></div></div>
              <div className="sbti-kpi"><div className="sbti-kpi__l">Regla GEVA</div><div className="sbti-kpi__v" style={{ fontSize: 15 }}>−{params.geva_anual ?? 7}% interanual mín.</div></div>
            </div>
            <div className="sbti-note sbti-note--warn">
              <b>Advertencia:</b> el efecto en emisiones absolutas depende del crecimiento del valor agregado. GEVA
              es menos robusto que contracción absoluta o intensidad física.
            </div>
            <Form>
              <Form.Field className="sbti-paso4__narrow">
                <label>Crecimiento anual estimado del valor agregado (%)</label>
                <Input type="number" step="0.1" value={s3.valueAddedGrowth || ""} onChange={(_, d) => setS3({ valueAddedGrowth: d.value })} />
              </Form.Field>
            </Form>
          </>
        )}

        {s3.method === "prov" && (
          <div className="sbti-note">
            <b>Compromiso de proveedores:</b> declarar qué % de emisiones (o de gasto anual de compras) cubre el
            compromiso; cumplimiento en ≤ 5 años desde la presentación; los proveedores deben fijar SBT alineados a
            los recursos del SBTi.
          </div>
        )}

        <p className="sbti-hint">{r3.detail}</p>
        <div className="sbti-note">
          <b>Redacción oficial generada:</b><br />ES — {t3.es}<br />EN — {t3.en}
        </div>
        <div className="sbti-src">
          <b>Fuentes:</b> guía sectorial 2019 — Tabla 3 (métodos A1+2), Tabla 4 (métodos A3), §4.2 y Tabla 5 (GEVA);
          Criterios V5.3.1 (ambiciones mínimas vigentes {params.tasa_15C ?? 4.2}% / {params.tasa_WB2C ?? 2.5}%; GEVA {params.geva_anual ?? 7}%).
        </div>
      </div>

      {/* ── Net-zero ── */}
      <div className="sbti-card">
        <h3><span className="sbti-tag">OPCIONAL</span> Net-Zero de largo plazo</h3>
        <Checkbox
          label="La empresa también fijará objetivo net-zero (Corporate Net-Zero Standard)"
          checked={!!nz.consider}
          onChange={(_, d) => setNz({ consider: d.checked })}
        />
        {nz.consider && (
          <>
            <div className="sbti-note">
              Requisitos del estándar vigente: reducción de al menos <b>{params.nz_reduccion ?? 90}%</b> de las
              emisiones en todos los alcances a <b>{params.nz_anio ?? 2050}</b> como máximo, cobertura A3 de largo
              plazo ≥ {params.nz_cobertura_a3 ?? 90}%, y neutralización de emisiones residuales con remociones
              permanentes. Para PYME es opcional.
            </div>
            <Form>
              <Form.Field>
                <label>Notas net-zero</label>
                <TextArea rows={2} value={nz.notes || ""} onChange={(_, d) => setNz({ notes: d.value })} />
              </Form.Field>
            </Form>
          </>
        )}
      </div>
    </div>
  );
}
